import React, { createRef } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { Button, Row, Col, FormGroup, FormControl, FormLabel } from "react-bootstrap";
import ExternalDropzone from "../common/ExternalDropzone";
import { extensionHandlers as extensions } from "../../FileHandlers/FileHandlers";
import "../../../../nav_bar/global_config/_reactTags.scss";
import {
  ID_PREFIX_FILE,
  ID_PREFIX_BOX,
  ID_PREFIX_SORTABLE_CONTAINER,
  FILE_UPLOAD_ERROR,
  FILE_UPLOADING,
} from "../../../../../../common/constants";
import { isFile, isSlide, isContainedView } from "../../../../../../common/utils";
import { createBox } from "../../../../../../common/common_tools";
import { randomPositionGenerator } from "../../../../clipboard/clipboard.utils";
import { isFileTooLarge, maxUploadMbFor } from "../../../../../../common/upload_limits.es6";
import { isClmAllowedPlugin } from "../../../../../../common/clm";

export default class UploadComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Multi-file queue: [{id, file, status: 'pending'|'uploading'|'done'|
      // 'error', percentage, preview, allowed, forbidden, uploadedData,
      // errorDetails}]
      queue: [],
      uploadingAll: false,
      filter: "",
      extensionFilter: this.props.show,
      keywords: [],
      errorDetails: null,
      showErrorDetails: false,
      copied: false,
    };
    this._rowSeq = 0;
    this.dropZoneRef = createRef();
    this.uploadHandler = this.uploadHandler.bind(this);
    this.dropHandler = this.dropHandler.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleAddition = this.handleAddition.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.addToScene = this.addToScene.bind(this);
    this.copyErrorReport = this.copyErrorReport.bind(this);
    this._onBoxMediaReady = this._onBoxMediaReady.bind(this);
    this._addTimeouts = {};
  }

  componentDidMount() {
    document.addEventListener("aidea-box-media-ready", this._onBoxMediaReady);
  }

  componentWillUnmount() {
    document.removeEventListener("aidea-box-media-ready", this._onBoxMediaReady);
    Object.values(this._addTimeouts).forEach(clearTimeout);
  }

  // The scene box dispatches this once its media finished loading — flip the
  // row's "+" button spinner back to normal.
  _onBoxMediaReady(evt) {
    const boxId = evt && evt.detail && evt.detail.boxId;
    if (!boxId) return;
    const row = this.state.queue.find((r) => r.status === "adding" && r.addingBoxId === boxId);
    if (!row) return;
    clearTimeout(this._addTimeouts[row.id]);
    delete this._addTimeouts[row.id];
    this._updateRow(row.id, { status: "done", addingBoxId: null });
  }

  _detectExtension(file) {
    let extension = file.type;
    for (let e in extensions) {
      let ext = extensions[e];
      if (file && file.type && file.type.match && file.type.match(ext.value)) {
        extension = ext.value;
      }
    }
    if (
      file.type === "application/vnd.ms-excel" ||
      file.type === "text/csv" ||
      file.name.toLowerCase().endsWith(".csv")
    ) {
      extension = "csv";
    }
    if (extension === file.type) {
      const fileName = file.name.toLowerCase();
      if (/\.(jpe?g|png|gif|webp)$/.test(fileName)) extension = "image";
      else if (/\.(mp4|avi|mov|webm)$/.test(fileName)) extension = "video";
      else if (/\.(mp3|wav|ogg)$/.test(fileName)) extension = "audio";
      else if (fileName.endsWith(".pdf")) extension = "pdf";
      else if (fileName.endsWith(".csv")) extension = "csv";
    }
    return extension;
  }

  // Non-playable video containers (avi/mkv/wmv/…) are transcoded to MP4
  // server-side on upload. Bu satırlarda kullanıcıyı önceden uyar (× ile iptal,
  // Upload ile onaylar). Playable olanlar (mp4/webm/mov/…) çevrilmez.
  _convertExt(row) {
    const name = (row && row.file && row.file.name) || "";
    const ext = name.split(".").pop().toLowerCase();
    const convertible = [
      "avi",
      "mkv",
      "wmv",
      "flv",
      "mpg",
      "mpeg",
      "3gp",
      "ts",
      "mts",
      "m2ts",
      "vob",
      "divx",
    ];
    return convertible.includes(ext) ? ext : null;
  }

  _updateRow(id, patch) {
    this.setState((s) => ({
      queue: s.queue.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  }
  renderQueueRow(row) {
    const sizeKb = row.file ? Math.max(1, Math.round(row.file.size / 1024)) : 0;
    const sizeLabel = sizeKb >= 1024 ? (sizeKb / 1024).toFixed(1) + " MB" : sizeKb + " KB";
    const blocked = !row.allowed || row.forbidden || row.tooLarge;
    return (
      <div
        key={row.id}
        className={
          "uploadQueue-row" +
          (row.status === "error" ? " error" : "") +
          (row.status === "done" ? " done" : "") +
          (row.tooLarge ? " tooLarge" : blocked ? " blocked" : "")
        }
      >
        <span
          className="uploadQueue-thumb"
          style={row.preview ? { backgroundImage: `url(${row.preview})` } : undefined}
        >
          {!row.preview && (
            <i className="material-icons" style={{ fontSize: 20 }}>
              {row.icon || "insert_drive_file"}
            </i>
          )}
        </span>
        <span className="uploadQueue-info">
          <span className="uploadQueue-name" title={row.file.name}>
            {row.file.name}
          </span>
          <span className="uploadQueue-meta">{sizeLabel}</span>
          {row.tooLarge ? (
            <span className="uploadQueue-note uploadQueue-tooLargeNote">
              <i className="material-icons">warning</i>
              {/* Sınır dosya tipine göre değişiyor (video 500 MB, diğerleri
                  100 MB) — mesajdaki sayı bu yüzden sabit değil, o dosyanın
                  gerçek sınırı. Sabit yazılınca 100 MB'da engellenen bir görsel
                  "500MB'den büyük olamaz" diyordu. */}
              {i18n.t("FileModal.APIProviders.warning_too_large", {
                max: maxUploadMbFor(row.file),
              })}
            </span>
          ) : null}
          {/* Hata mesajı satırın içinde görünür — eskiden yalnızca küçük bir
              kırmızı ikon vardı ve mesajı görmek için ona tıklamak gerekiyordu,
              yani 429 gibi hatalar pratikte "sessizce" başarısız oluyordu. */}
          {row.status === "error" ? (
            <span
              className="uploadQueue-errorNote"
              style={{ fontSize: 11, color: "#b3372f", marginTop: 3, display: "block" }}
            >
              {(row.errorDetails && (row.errorDetails.userMessage || row.errorDetails.message)) ||
                i18n.t("FileModal.APIProviders.error")}
            </span>
          ) : null}
          {row.status !== "done" && row.status !== "error" && this._convertExt(row) ? (
            <span className="uploadQueue-note uploadQueue-convertNote">
              <i className="material-icons">autorenew</i>
              {i18n.t("FileModal.APIProviders.VideoGeneration.ConvertNote", {
                ext: this._convertExt(row),
              })}
            </span>
          ) : null}
        </span>
        <span className="uploadQueue-status">
          {blocked ? (
            <i
              className="material-icons"
              style={{ fontSize: 18, color: row.tooLarge ? "#dc2626" : "#b98900" }}
              title={i18n.t(
                row.tooLarge
                  ? "FileModal.APIProviders.warning_too_large"
                  : row.forbidden
                    ? "FileModal.APIProviders.warning_forbidden"
                    : "FileModal.APIProviders.warning_allowed",
                row.tooLarge ? { max: maxUploadMbFor(row.file) } : undefined
              )}
            >
              warning
            </i>
          ) : row.status === "uploading" ? (
            <span>{row.percentage || "%0"}</span>
          ) : row.status === "adding" ? (
            <span
              className="uploadQueue-addBtn uploadQueue-addBtn--busy"
              title={i18n.t("FileModal.APIProviders.addingToScene")}
            >
              <span className="uploadQueue-spinner" />
            </span>
          ) : row.status === "done" ? (
            <button
              type="button"
              className="uploadQueue-addBtn"
              onClick={() => this.addToScene(row)}
              title={i18n.t("FileModal.APIProviders.addToScene")}
            >
              <i className="material-icons">add</i>
            </button>
          ) : row.status === "error" ? (
            <i
              className="material-icons fail"
              style={{ fontSize: 18, cursor: "pointer" }}
              role="button"
              title={i18n.t("FileModal.APIProviders.errorDetailsTitle")}
              onClick={() =>
                this.setState({ errorDetails: row.errorDetails, showErrorDetails: true })
              }
            >
              error_outline
            </i>
          ) : null}
        </span>
        {row.status === "pending" || row.status === "error" || blocked ? (
          <button
            type="button"
            className="uploadQueue-remove"
            title={i18n.t("FileModal.APIProviders.clear")}
            onClick={() =>
              this.setState((s) => ({ queue: s.queue.filter((r) => r.id !== row.id) }))
            }
          >
            <i className="material-icons" style={{ fontSize: 16 }}>
              close
            </i>
          </button>
        ) : null}
      </div>
    );
  }

  render() {
    const { queue, uploadingAll } = this.state;
    const pendingCount = queue.filter(
      (r) => r.status === "pending" && r.allowed && !r.forbidden && !r.tooLarge
    ).length;
    const doneRows = queue.filter((r) => r.status === "done" && r.uploadedData);

    return (
      <div className="contentComponent uploadComponent">
        <h5>{i18n.t("FileModal.APIProviders.UploadFilesTitle")}</h5>
        <hr />

        {/* ── Dropzone: always visible so more files can be queued anytime ── */}
        <ExternalDropzone
          ref={this.dropZoneRef}
          accept={this.props.show}
          multiple
          callback={this.dropHandler}
        />

        {/* ── Upload queue ── */}
        {queue.length > 0 ? (
          <div className="upload-result-panel">
            <div className="uploadQueue">{queue.map((row) => this.renderQueueRow(row))}</div>

            {/* Error details — opened from a failed row's error icon */}
            {this.state.showErrorDetails && this.state.errorDetails ? (
              <div
                id="errorMsg"
                className="uploadModalMsg"
                style={{ flexDirection: "column", alignItems: "center", justifyContent: "center" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="material-icons" style={{ color: "#e04b4b" }}>
                    error_outline
                  </i>
                  <div>{i18n.t("FileModal.APIProviders.error")}</div>
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#888",
                    marginTop: "4px",
                    textAlign: "center",
                    maxWidth: "320px",
                  }}
                >
                  {i18n.t("FileModal.APIProviders.errorHint")}
                </div>

                {this.state.errorDetails ? (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "12px",
                      width: "100%",
                      maxWidth: "360px",
                      background: "#fff8ec",
                      border: "1px solid #f5a623",
                      borderRadius: "8px",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: "4px", color: "#7a5200" }}>
                      {i18n.t("FileModal.APIProviders.errorReportTitle")}
                    </div>
                    <div style={{ fontSize: "12px", color: "#7a5200", marginBottom: "8px" }}>
                      {i18n.t("FileModal.APIProviders.errorReportIntro")}
                    </div>
                    <textarea
                      readOnly
                      value={this.buildErrorReport()}
                      onFocus={(e) => e.target.select()}
                      style={{
                        width: "100%",
                        height: "150px",
                        fontFamily: "monospace",
                        fontSize: "11px",
                        color: "#333",
                        background: "#fff",
                        border: "1px solid #e0c48a",
                        borderRadius: "6px",
                        padding: "8px",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                      <Button size="sm" variant="outline-secondary" onClick={this.copyErrorReport}>
                        <i className="material-icons" style={{ fontSize: "16px" }}>
                          content_copy
                        </i>{" "}
                        {this.state.copied
                          ? i18n.t("FileModal.APIProviders.copied")
                          : i18n.t("FileModal.APIProviders.copyReport")}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Action buttons */}
            <div
              className="upload-action-buttons"
              style={{
                display: "flex",
                gap: "8px",
                marginTop: "14px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {pendingCount > 0 ? (
                <Button disabled={uploadingAll} variant="primary" onClick={this.uploadHandler}>
                  <i className="material-icons">file_upload</i>{" "}
                  {i18n.t("FileModal.APIProviders.upload")}
                  {pendingCount > 1 ? ` (${pendingCount})` : ""}
                </Button>
              ) : null}
              {doneRows.length > 1 ? (
                <Button
                  variant="success"
                  disabled={uploadingAll}
                  style={{ fontWeight: 600 }}
                  onClick={() => doneRows.forEach((row) => this.addToScene(row))}
                >
                  <i className="material-icons">add_to_queue</i>{" "}
                  {i18n.t("FileModal.APIProviders.addAllToScene")}
                </Button>
              ) : null}
              <Button
                variant="outline-secondary"
                disabled={uploadingAll}
                onClick={() =>
                  this.setState({ queue: [], errorDetails: null, showErrorDetails: false })
                }
              >
                <i className="material-icons">clear</i> {i18n.t("FileModal.APIProviders.clear")}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.show !== this.props.show) {
      this.setState({ extensionFilter: nextProps.show });
    }
    if (
      nextProps.isBusy &&
      this.props.isBusy &&
      nextProps.isBusy.value !== this.props.isBusy.value
    ) {
      if (nextProps.isBusy.msg === FILE_UPLOADING && this.props.isBusy.msg !== FILE_UPLOADING) {
        // this.setState({error: false, uploading: true, uploaded: false})
      } else if (
        this.props.isBusy.msg === FILE_UPLOADING &&
        nextProps.isBusy.msg === FILE_UPLOAD_ERROR
      ) {
        // Legacy redux upload flow: per-row status is driven by the
        // uploadFunction callbacks now; nothing to mirror here.
      } else if (isFile(nextProps.isBusy.msg)) {
        let newFile = nextProps.filesUploaded[nextProps.isBusy.msg];

        let extension = newFile && newFile.mimetype ? newFile.mimetype : "";

        // First try to match by mimetype
        for (let e in extensions) {
          let ext = extensions[e];
          if (
            newFile &&
            newFile.mimetype &&
            newFile.mimetype.match &&
            newFile.mimetype.match(ext.value)
          ) {
            extension = ext.value;
          }
          if (
            newFile &&
            newFile.mimetype &&
            (newFile.mimetype === "application/vnd.ms-excel" || newFile.mimetype === "text/csv")
          ) {
            extension = "csv";
          }
        }

        this.props.onElementSelected(
          newFile ? newFile.name : "",
          newFile ? newFile.url : "",
          extension,
          nextProps.isBusy.msg
        );
        this.setState({ errorDetails: null, showErrorDetails: false });
      }
    }
  }
  // Accepts a File or File[] (multi-select / multi-drop) and appends rows to
  // the queue. Image rows get an async thumbnail preview.
  dropHandler(files) {
    const list = Array.isArray(files) ? files : [files];
    const rows = list.map((file) => {
      const extension = this._detectExtension(file);
      const allowed = this.props.show === "*" || !!this.props.show.match(extension);
      const forbidden = !!(
        IdealStudio.Config.zip_files_forbidden &&
        file.name &&
        (file.name.match(/\.zip$/) || file.name.match(/\.edi/) || file.name.match(/\.vish$/))
      );
      // Sınırı aşan dosya kuyruğa girer ama yüklenmez (transkod maliyeti +
      // depolama). Sınır tipe göre: video 500 MB, diğerleri 100 MB —
      // upload_limits.es6 çözüyor, backend aynası
      // IdealStudioTrainingService.MAX_VIDEO_FILE_SIZE_BYTES.
      const tooLarge = isFileTooLarge(file);
      let icon = "attach_file";
      for (let e in extensions) {
        const ext = extensions[e];
        if (file.type && file.type.match && file.type.match(ext.value)) {
          icon = ext.icon || icon;
        }
      }
      return {
        id: "up-" + ++this._rowSeq,
        file,
        extension,
        allowed,
        forbidden,
        tooLarge,
        icon,
        status: "pending",
        percentage: "%0",
        preview: null,
        uploadedData: null,
        errorDetails: null,
      };
    });
    this.setState((s) => ({ queue: [...s.queue, ...rows] }));
    rows.forEach((row) => {
      if (row.extension === "image") {
        const reader = new FileReader();
        reader.readAsDataURL(row.file);
        reader.onloadend = () => this._updateRow(row.id, { preview: reader.result });
      }
    });
  }

  // Uploads every eligible queued row SEQUENTIALLY through the single-file
  // uploadFunction, tracking per-row progress/status.
  uploadHandler() {
    if (this.state.uploadingAll) return;
    this.setState({ uploadingAll: true, errorDetails: null, showErrorDetails: false }, () =>
      this._uploadNext()
    );
  }

  _uploadNext() {
    const next = this.state.queue.find(
      (r) => r.status === "pending" && r.allowed && !r.forbidden && !r.tooLarge
    );
    if (!next) {
      this.setState({ uploadingAll: false }, () => this._afterAllUploads());
      return;
    }
    this._updateRow(next.id, { status: "uploading", percentage: "%0" });
    const file = next.file;
    this.props.uploadFunction(
      file,
      "",
      (response, errorInfo) => {
        // Normalize response: support both JSON object and plain URL string
        let fileUrl = null;
        let fileName = file.name;
        let mimeType = file.type;
        let fileSize = file.size;
        let fileExtension = "";

        if (response && typeof response === "object" && response.success && response.fileUrl) {
          fileUrl = response.fileUrl;
          fileName = response.fileName || fileName;
          mimeType = response.mimeType || mimeType;
          fileSize = response.fileSize || fileSize;
          fileExtension = response.fileExtension || fileExtension;
        } else if (response && typeof response === "string" && response.startsWith("http")) {
          fileUrl = response;
        }

        // Normalize mimeType to extension handler type (e.g. "video/mp4" → "video")
        let normalizedType = mimeType;
        for (let e in extensions) {
          let ext = extensions[e];
          if (mimeType && mimeType.match && mimeType.match(ext.value)) {
            normalizedType = ext.value;
          }
        }
        if (mimeType === "application/vnd.ms-excel" || mimeType === "text/csv") {
          normalizedType = "csv";
        }

        if (fileUrl) {
          this._updateRow(next.id, {
            status: "done",
            uploadedData: { fileUrl, fileName, mimeType, normalizedType, fileSize, fileExtension },
          });
        } else {
          this._updateRow(next.id, { status: "error", errorDetails: errorInfo || null });
        }
        this._uploadNext();
      },
      (percentage) => {
        this._updateRow(next.id, { percentage });
      }
    );
  }

  // Single-file compatibility: with exactly ONE successful upload the old
  // contract still applies — file modal replace flows target a specific box.
  _afterAllUploads() {
    const doneRows = this.state.queue.filter((r) => r.status === "done" && r.uploadedData);
    if (doneRows.length !== 1) return;
    const { fileUrl, fileName, normalizedType } = doneRows[0].uploadedData;
    if (this.props.fileModalResult && this.props.fileModalResult.id) {
      let isMatch = true;
      if (this.props.fileModalResult.accept && normalizedType) {
        isMatch = this.props.fileModalResult.accept
          .toLowerCase()
          .includes(normalizedType.toLowerCase());
      }

      if (isMatch) {
        if (typeof this.props.onElementSelected === "function") {
          this.props.onElementSelected(
            fileName,
            fileUrl,
            normalizedType,
            this.props.fileModalResult.id
          );
        } else if (typeof this.props.close === "function") {
          this.props.close({
            id: this.props.fileModalResult.id,
            value: fileUrl,
            structure: { height: "auto" },
          });
        }
      } else {
        this.addToScene(doneRows[0]);
      }
    }
  }

  /** Build a copy-pasteable, non-sensitive technical error report for support. */
  buildErrorReport() {
    const d = this.state.errorDetails || {};
    const lines = ["=== Aidea Studio — Upload Error Report ==="];
    if (d.time) lines.push("Time: " + d.time);
    lines.push(
      "File: " +
        (d.fileName || "-") +
        (d.fileSize != null
          ? " (" + d.fileSize + " bytes" + (d.fileType ? ", " + d.fileType : "") + ")"
          : "")
    );
    lines.push("Request: " + (d.method || "POST") + " " + (d.endpoint || "-"));
    if (d.status != null) {
      lines.push("Server response: " + d.status + " " + (d.statusText || ""));
    }
    if (d.message) lines.push("Error: " + d.message);
    if (d.responseBody) lines.push("Response body: " + d.responseBody);
    if (d.fallbackError) lines.push("Note: " + d.fallbackError);
    return lines.join("\n");
  }

  copyErrorReport() {
    const text = this.buildErrorReport();
    const done = () => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  }

  /** *
   * Keyword deleted callback
   * @param i position of the keyword
   */
  handleDelete(i) {
    let tags = Object.assign([], this.state.keywords);
    tags.splice(i, 1);
    this.setState({ keywords: tags });
  }

  /**
   * Keyword added callback
   * @param tag Keyword name
   */
  handleAddition(tag) {
    let tags = Object.assign([], this.state.keywords);
    tags.push({
      id: tags.length + 1,
      text: tag,
    });
    this.setState({ keywords: tags });
  }

  /**
   * Keyword moved callback
   * @param tag Tag moving
   * @param currPos Current position
   * @param newPos New position
   */
  handleDrag(tag, currPos, newPos) {
    let tags = Object.assign([], this.state.keywords);

    // mutate array
    tags.splice(currPos, 1);
    tags.splice(newPos, 0, tag);
    // re-render
    this.setState({ keywords: tags });
  }

  addToScene(row) {
    const data = row && row.uploadedData ? row.uploadedData : null;
    if (!data) return;
    const { fileUrl, normalizedType } = data;

    // Determine current page (same logic as FileModal.currentPage)
    const { containedViewSelected, containedViews, navItemSelected, navItems, boxes, onBoxAdded } =
      this.props;

    let page;
    if (isContainedView(containedViewSelected)) {
      page = containedViews[containedViewSelected];
    } else if (navItemSelected !== 0) {
      page = navItems[navItemSelected];
    }

    if (!page) {
      console.error("🚀 [addToScene] No page selected — cannot add to scene!");
      return;
    }

    // Look up which plugins handle this file type
    const pluginsAllowed = IdealStudio.Config.pluginFileMap[normalizedType] || {};

    const isTargetSlide = isSlide(page.type);
    const parent = isTargetSlide ? page.id : page.boxes[0];
    // Date.now() alone collides when "add all" creates several boxes in the
    // same millisecond — offset by a per-instance counter.
    this._boxSeq = (this._boxSeq || 0) + 1;
    const uniqueStamp = Date.now() + this._boxSeq;
    const container = isTargetSlide ? 0 : ID_PREFIX_SORTABLE_CONTAINER + uniqueStamp;

    const preferredOrder = ["HotspotImages", "EnrichedPlayer", "EnrichedAudio", "EnrichedPDF"];
    let targetPluginName = null;

    // CLM courses: only allowlisted plugins may be added (spec §3.3 item 1) —
    // drops EnrichedPlayer/EnrichedAudio/EnrichedPDF from the candidates so a
    // video/audio/pdf upload has nothing to attach to and is silently skipped.
    const allowedNames = Object.keys(pluginsAllowed).filter(
      (name) => !this.props.isClm || isClmAllowedPlugin(name)
    );
    if (allowedNames.length > 0) {
      for (let p of preferredOrder) {
        if (allowedNames.includes(p)) {
          targetPluginName = p;
          break;
        }
      }
      if (!targetPluginName) {
        targetPluginName = allowedNames[0];
      }
    }

    if (targetPluginName) {
      const key = pluginsAllowed[targetPluginName];
      const ids = {
        id: ID_PREFIX_BOX + uniqueStamp,
        parent,
        container,
        row: 0,
        col: 0,
        index: undefined,
        page: page.id,
        position: isTargetSlide
          ? {
              type: "absolute",
              x: randomPositionGenerator(20, 40),
              y: randomPositionGenerator(20, 40),
            }
          : { type: "relative", x: "0%", y: "0%" },
        initialState: { [key]: fileUrl },
      };

      // Videos keep loading in the scene box after createBox returns — show a
      // spinner on the row's "+" button until the box reports media ready
      // (aidea-box-media-ready), with a fallback timeout so it never sticks.
      if (targetPluginName === "EnrichedPlayer" && row.id) {
        this._updateRow(row.id, { status: "adding", addingBoxId: ids.id });
        clearTimeout(this._addTimeouts[row.id]);
        this._addTimeouts[row.id] = setTimeout(() => {
          delete this._addTimeouts[row.id];
          this._updateRow(row.id, { status: "done", addingBoxId: null });
        }, 30000);
      }

      createBox(ids, targetPluginName, isTargetSlide, onBoxAdded, boxes);
    } else {
      console.warn("[addToScene] No compatible plugin found for type:", normalizedType);
    }

    // Close modal after adding
    if (this.props.closeSideBar) {
      this.props.closeSideBar(true);
    }
  }
}

UploadComponent.propTypes = {
  /**
   * Format allowed
   */
  show: PropTypes.any,
  /**
   * Files uploaded to server
   */
  filesUploaded: PropTypes.any,
  /**
   * File selected id
   */
  idSelected: PropTypes.any,
  /**
   * Selects Element
   */
  onElementSelected: PropTypes.func.isRequired,
  /**
   * Server busy
   */
  isBusy: PropTypes.any,
  /**
   * Uploads file to server
   */
  uploadFunction: PropTypes.any,
  /**
   * Whether this is a CLM course — only allowlisted-plugin uploads may be added
   */
  isClm: PropTypes.bool,
};
